// Bands — a paginated list grouped on the SERVER (ADR-078 §4 · ADR-040 §6):
// the shape one band travels in, the two tokens a band list reserves, and the
// LADDER — the calendar words a time axis bands by.
//
// A grouped read answers every band of the filtered population with its TRUE
// count (one GROUP BY), plus the STREAM: the rows of the bands the person
// OPENED, in band order then the list's sort, 100 a page, each row stamped
// with its band's key. A band's key is the data's own id (a person · a zone ·
// a model), a vocabulary's code (a chemistry), a derived word (a solution
// type) or a ladder word; its label travels with the band list when the
// server holds it (the person's name — joined once in the GROUP BY) and is
// null when the screen prints the word itself. The opened keys ride the URL
// (`open=today,u_ahmad`; `open=*` = every band) and the wire as a CSV.
import { z } from 'zod';
/** A band's key — an id, a vocabulary's code, a derived word, a ladder word, or a reserved token; never empty, and
 *  never a NUL byte (a key is bound as text: the database refuses one). */
export const BandKeySchema = z.string().min(1).max(32).regex(/^[^\u0000]+$/);
/** The band of the rows with NO value on the axis ("not set" — no one, no
 *  zone): the server keys it so, lists it LAST by default, and leaves its
 *  label null — the surface words it ("No one"). */
export const BAND_NOT_SET = 'none';
/** `open=*` — every band open (the gear's "Expand all"); absent = every band folded (how a grouped view arrives). */
export const BAND_ALL = '*';
export const BandSchema = z
    .object({
    key: BandKeySchema,
    /** The band's name when the server holds it (a data-keyed axis); null for a word the screen prints by key. */
    label: z.string().nullable(),
    /** The band's rows under the list's filters — exact, or a FLOOR when `capped`. */
    count: z.number().int().nonnegative(),
    /** The count is a floor: the list's history is counted through ONE capped walk (ADR-078 §2 — the newest
     *  `HISTORY_COUNT_CAP + 1` filed rows of the window), and the walk ended before it could finish this band's
     *  rows — the screen prints "n+". Never true on a list with no history. */
    capped: z.boolean(),
})
    .strict();
/** The LADDER — the calendar words a TIME axis bands by, newest first (ADR-082
 *  §4's calendar words, then `older` for the rest). Each band is its word's
 *  period MINUS what the bands above took (the server cuts them in the screen's
 *  zone on the project's week — `core/time`); a surface keeps the rungs it
 *  needs, in this order — a PAST rung only under its current twin (yesterday
 *  under today · previous_week under this_week · previous_month under
 *  this_month · previous_year under this_year), `older` always last — and
 *  never adds one. Empty rungs are not listed. A forward ladder (due dates:
 *  overdue · today · tomorrow · later) joins the vocabulary with its first
 *  consumer. */
export const LADDER_WORDS = ['today', 'yesterday', 'this_week', 'previous_week', 'this_month', 'previous_month', 'this_year', 'previous_year', 'older'];
export const isLadderWord = (v) => LADDER_WORDS.includes(v);
